import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { apiGet, apiSend } from '@jmvilaca-corp/auth-ui'
import { eventsKey, type CalendarSummary } from './calendars'
import { connectorStatusOptions, standardSource, statusKey, type ConnectionState, type ConnectionStatus } from './connectors'
import type { SyncWindow } from './dates'
import { formatName } from './names'

/** Ce que l'icone d'un calendrier lie montre. */
export type CalendarLinkState = 'connecting' | 'disconnected' | 'syncing' | 'synced'

interface SyncOutcome {
  state: 'synced' | Exclude<ConnectionState, 'connected'>
  /** Evenements crees, ou que le fournisseur a modifies depuis la derniere fois. */
  imported: number
  /** Evenements a venir que le fournisseur ne liste plus, marques annules. */
  canceled: number
  /** Evenements dont le detail reste a relever : rappeler la meme periode continue. */
  pending: number
  /** Evenements dont le detail vient d'etre relu. */
  refreshed: number
}

export interface SportCoricoConvocation {
  firstName: string
  lastName: string
  /** Le convoque a confirme sa presence. */
  present: boolean
  /** `null` tant qu'il n'a pas repondu. */
  respondedAt: string | null
  absenceJustification: string | null
}

export interface SportCoricoTodo {
  title: string
  completed: boolean
  assignees: string[]
}

export interface SportCoricoChatMessage {
  author: string
  content: string
  sentAt: string | null
}

export interface SportCoricoComposition {
  name: string | null
  /** Schema de jeu : « 3-5-2 ». */
  formation: string | null
  /**
   * Dans l'ordre du schema, du gardien a l'attaque. `x` et `y` placent le
   * poste sur le terrain, de 0 a 100 : `y` va de sa propre ligne de but a
   * celle d'en face. `null` si SportCorico ne les donne pas.
   */
  starters: { position: string; name: string; x: number | null; y: number | null }[]
  substitutes: string[]
}

export interface SportCoricoLiveAction {
  action: string
  /** Minute de jeu, la premiere valant 1. */
  minute: number | null
  period: number | null
  /** L'action est celle de l'equipe qui recoit. */
  isHome: boolean
  player: string | null
  secondPlayer: string | null
}

export interface SportCoricoRankingRow {
  rank: number
  team: string
  points: number
  played: number
  won: number
  drawn: number
  lost: number
  scored: number
  conceded: number
  difference: number
}

/** Ce qu'un match ajoute au detail d'un evenement. */
export interface SportCoricoMatch {
  homeTeam: string | null
  outsideTeam: string | null
  /** L'equipe du calendrier recoit ; `null` si SportCorico ne le dit pas. */
  isHome: boolean | null
  competition: string | null
  /** Resultat du championnat, a defaut score du live ; `null` tant que le match n'a pas de score. */
  homeScore: number | null
  outsideScore: number | null
  composition: SportCoricoComposition | null
  liveActions: SportCoricoLiveAction[]
  /** Qui a vote, et pour qui. */
  menOfTheGame: { voter: string; elected: string }[]
  /** Note donnee au match par chaque joueur. */
  gameRatings: { voter: string; note: number }[]
  ranking: SportCoricoRankingRow[]
  otherMatches: {
    homeTeam: string
    outsideTeam: string
    homeScore: number | null
    outsideScore: number | null
    playedAt: string | null
  }[]
  /** Titularisation et temps de jeu ; `minutes` est `null` quand SportCorico ne le renseigne pas. */
  playerStats: { player: string; starter: boolean; minutes: number | null }[]
}

/** Ce que SportCorico dit d'un evenement importe, au-dela de ses proprietes de base. */
export interface SportCoricoEvent {
  eventId: string
  /** Identifiant de l'evenement chez SportCorico. */
  externalId: string | null
  eventType: string
  eventTypeLabel: string | null
  meetAt: string | null
  ownerFirstName: string | null
  ownerLastName: string | null
  convocations: SportCoricoConvocation[]
  todos: SportCoricoTodo[]
  chatMessages: SportCoricoChatMessage[]
  syncedAt: string
  /** Derniere lecture du detail ; `null` tant que responsable, convoques, taches et chat n'ont pas ete releves. */
  detailsSyncedAt: string | null
  /** Derniere fois ou une lecture a rapporte du neuf : SportCorico ne date pas ses modifications. */
  changedAt: string
  /** `null` pour un evenement qui n'est pas un match. */
  match: SportCoricoMatch | null
}

// Une periode deja mise a jour ne l'est pas a nouveau a chaque aller-retour
// dans le calendrier : chaque synchronisation interroge vraiment le fournisseur.
const syncFreshness = 5 * 60 * 1000

const maxDetailRounds = 6

function syncOnce(calendarId: string, window: SyncWindow) {
  return apiSend<SyncOutcome>(`/api/calendars/${calendarId}/sync`, 'POST', window)
}

/**
 * Met a jour en arriere-plan les calendriers lies que l'utilisateur voit, et
 * dit ou en est chacun. Les periodes sont traitees dans l'ordre donne : celle
 * affichee d'abord.
 *
 * Le serveur rejoue toujours le compte lie du proprietaire. Le proprietaire
 * attend que son connecteur reponde ; un invite n'a pas de compte a
 * verifier : c'est la synchronisation qui lui dit si la connexion tient.
 */
export function useCalendarSync(calendars: CalendarSummary[], windows: SyncWindow[]): Map<string, CalendarLinkState> {
  const queryClient = useQueryClient()
  const linked = calendars.filter((calendar) => calendar.source !== standardSource)
  const connectors = [...new Set(linked.filter((calendar) => calendar.role === 'owner').map((calendar) => calendar.source))]

  const statuses = useQueries({ queries: connectors.map(connectorStatusOptions) })
  const stateOf = (connector: string) => statuses[connectors.indexOf(connector)]

  const syncs = useQueries({
    queries: linked.map((calendar) => ({
      queryKey: ['calendar-sync', calendar.calendarId, windows.map((window) => `${window.from}_${window.to}`).join('|')],
      enabled: calendar.role !== 'owner' || stateOf(calendar.source)?.data?.state === 'connected',
      staleTime: syncFreshness,
      queryFn: async () => {
        for (const window of windows) {
          const windowKey = ['calendar-sync-window', calendar.calendarId, window.from, window.to]
          const outcome = await queryClient.fetchQuery({
            queryKey: windowKey,
            staleTime: syncFreshness,
            queryFn: async () => {
              // Ce qui est a l'ecran n'est relu que si le fournisseur a dit
              // quelque chose de neuf : la plupart des passages ne changent rien.
              const sync = async () => {
                const outcome = await syncOnce(calendar.calendarId, window)
                if (outcome.state === 'synced' && outcome.imported + outcome.canceled + outcome.refreshed > 0) {
                  void queryClient.invalidateQueries({ queryKey: eventsKey })
                }
                return outcome
              }

              // Le serveur releve les details par lots : la meme periode est
              // rappelee tant qu'il en reste et que leur nombre diminue.
              let result = await sync()
              for (let round = 1; result.state === 'synced' && result.pending > 0 && round < maxDetailRounds; round++) {
                const next = await sync()
                if (next.state !== 'synced' || next.pending >= result.pending) {
                  return next
                }
                result = next
              }

              return result
            },
          })

          if (outcome.state !== 'synced') {
            // Un echec ne se garde pas : il serait rendu tel quel une fois la connexion retablie.
            queryClient.removeQueries({ queryKey: windowKey })
            const state = outcome.state
            // L'etat du connecteur est celui du compte de l'utilisateur : l'echec
            // du compte d'un autre ne le concerne pas.
            if (calendar.role === 'owner') {
              queryClient.setQueryData<ConnectionStatus>(statusKey(calendar.source), (previous) => ({
                state,
                login: previous?.login ?? null,
              }))
            }
            throw new Error(`Synchronisation interrompue : ${state}.`)
          }
        }

        return true
      },
    })),
  })

  return new Map(
    linked.map((calendar, index) => {
      const sync = syncs[index]

      if (calendar.role === 'owner') {
        const status = stateOf(calendar.source)
        if (status === undefined || status.isPending) {
          return [calendar.calendarId, 'connecting']
        }

        if (status.data?.state !== 'connected') {
          return [calendar.calendarId, 'disconnected']
        }
      }

      if (sync === undefined || sync.isError) {
        return [calendar.calendarId, 'disconnected']
      }

      return [calendar.calendarId, sync.isSuccess && !sync.isFetching ? 'synced' : 'syncing']
    }),
  )
}

/**
 * Les noms de personnes, mis en forme : SportCorico les rend tels que chacun
 * les a saisis. Fait ici, une fois, pour que les rapprochements par nom
 * (temps de jeu d'un convoque, d'un titulaire) comparent des noms ecrits pareil.
 * Les noms d'equipes ne sont pas touches.
 */
export function withFormattedNames(event: SportCoricoEvent): SportCoricoEvent {
  const optional = (name: string | null) => (name === null ? null : formatName(name))
  const { match } = event

  return {
    ...event,
    ownerFirstName: optional(event.ownerFirstName),
    ownerLastName: optional(event.ownerLastName),
    convocations: event.convocations.map((convocation) => ({
      ...convocation,
      firstName: formatName(convocation.firstName),
      lastName: formatName(convocation.lastName),
    })),
    todos: event.todos.map((todo) => ({ ...todo, assignees: todo.assignees.map(formatName) })),
    chatMessages: event.chatMessages.map((message) => ({ ...message, author: formatName(message.author) })),
    match:
      match === null
        ? null
        : {
            ...match,
            composition:
              match.composition === null
                ? null
                : {
                    ...match.composition,
                    starters: match.composition.starters.map((starter) => ({ ...starter, name: formatName(starter.name) })),
                    substitutes: match.composition.substitutes.map(formatName),
                  },
            liveActions: match.liveActions.map((action) => ({
              ...action,
              player: optional(action.player),
              secondPlayer: optional(action.secondPlayer),
            })),
            menOfTheGame: match.menOfTheGame.map((vote) => ({ voter: formatName(vote.voter), elected: formatName(vote.elected) })),
            gameRatings: match.gameRatings.map((rating) => ({ ...rating, voter: formatName(rating.voter) })),
            playerStats: match.playerStats.map((stat) => ({ ...stat, player: formatName(stat.player) })),
          },
  }
}

/**
 * Ce qui est en base s'affiche aussitot. Le detail est en plus relu chez
 * SportCorico a l'ouverture, sans attendre son tour dans la mise a jour du
 * calendrier : c'est l'evenement qu'on regarde.
 */
export function useSportCoricoEvent(eventId: string) {
  const stored = useQuery({
    queryKey: [...eventsKey, eventId, 'sportcorico'] as const,
    queryFn: () => apiGet<SportCoricoEvent>(`/api/events/${eventId}/sportcorico`),
  })
  const fresh = useQuery({
    queryKey: [...eventsKey, eventId, 'sportcorico', 'fresh'] as const,
    queryFn: () => apiSend<SportCoricoEvent>(`/api/events/${eventId}/sportcorico/refresh`, 'POST'),
    // Le serveur ne relit pas non plus un detail lu dans la minute.
    staleTime: 60 * 1000,
  })

  const raw = fresh.data ?? stored.data
  const data = useMemo(() => (raw === undefined ? undefined : withFormattedNames(raw)), [raw])

  return {
    data,
    isPending: stored.isPending && fresh.data === undefined,
    error: fresh.data === undefined && stored.data === undefined ? (stored.error ?? fresh.error) : null,
    isRefreshing: fresh.isFetching,
  }
}
